// client/src/coms/views/canvas_parts/CanvasDistortionSettingsPanel.js
import React, { useState } from 'react';
import {
  DEFAULT_LIQUIFY_SETTINGS,
  DISTORTION_MODES,
  normaliseLiquifyMode,
  normaliseLiquifySettings,
} from './canvas_room_view_parts/liquifyUtils';

const DISTORTION_SETTING_HINTS = {
  mode: 'Choose the distortion behaviour. Smudge now lives here as one of the distortion modes.',
  flow: 'How much of the stroke movement is fed into the distortion.',
  twist: 'Extra rotational pull inside the brush.',
  chaos: 'Adds noisy broken movement to the warp.',
  detail: 'Controls the fine pattern scale used by organic modes.',
  edge: 'Controls how much of the distortion is pushed toward the brush rim.',
  size: 'Controls the diameter of the distortion brush.',
  reset: 'Reset distortion mode and sliders back to their defaults.',
};

function clampPercent(value, fallback = 0) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(0, Math.min(100, Math.round(number)));
}

function makeTooltipPosition(element) {
  if (!element || typeof element.getBoundingClientRect !== 'function') {
    return { left: 0, top: 0 };
  }

  const rect = element.getBoundingClientRect();
  const tooltipWidth = 236;
  const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - tooltipWidth - 8));
  const top = Math.min(rect.bottom + 8, Math.max(8, window.innerHeight - 68));
  return { left, top };
}

function PanelHint({ hint }) {
  if (!hint) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: hint.left,
        top: hint.top,
        zIndex: 10050,
        width: 236,
        maxWidth: 'calc(100vw - 16px)',
        pointerEvents: 'none',
        borderRadius: 9,
        border: '1px solid rgba(255,255,255,0.14)',
        background: 'rgba(15,23,42,0.98)',
        boxShadow: '0 12px 34px rgba(0,0,0,0.38)',
        color: 'rgba(255,255,255,0.9)',
        padding: '7px 9px',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.35,
      }}
    >
      {hint.text}
    </div>
  );
}

function PanelSlider({ label, value, onChange, hint, onShowHint, onHideHint, min = 0, max = 100, step = 1, suffix = '%', normaliseValue = null }) {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? Math.max(min, Math.min(max, numericValue)) : min;

  function showHint(event) {
    if (typeof onShowHint !== 'function') return;
    onShowHint(hint || label, event.currentTarget);
  }

  return (
    <label
      title={hint || label}
      onMouseEnter={showHint}
      onMouseLeave={onHideHint}
      onFocus={showHint}
      onBlur={onHideHint}
      style={{
        display: 'grid',
        gridTemplateColumns: '58px minmax(0, 1fr) 42px',
        alignItems: 'center',
        gap: 8,
        minHeight: 28,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 850,
          color: 'rgba(255,255,255,0.72)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={safeValue}
        onChange={(event) => {
          if (typeof onChange !== 'function') return;
          const nextValue = Number(event.target.value);
          onChange(typeof normaliseValue === 'function' ? normaliseValue(nextValue) : nextValue / 100);
        }}
        style={{
          width: '100%',
          accentColor: '#f5f5f5',
        }}
      />
      <span
        style={{
          fontSize: 10,
          fontWeight: 850,
          color: 'rgba(255,255,255,0.62)',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {Math.round(safeValue)}{suffix}
      </span>
    </label>
  );
}

export default function CanvasDistortionSettingsPanel({
  activeTool,
  liquifyMode,
  setLiquifyMode,
  liquifySettings,
  setLiquifySettings,
  brushSize,
  setBrushSize,
}) {
  const [hoverHint, setHoverHint] = useState(null);

  if (activeTool !== 'liquify') return null;

  const safeMode = normaliseLiquifyMode(liquifyMode);
  const safeSettings = normaliseLiquifySettings(liquifySettings);
  const activeMode = DISTORTION_MODES.find((mode) => mode.id === safeMode) || DISTORTION_MODES[0];

  function showHint(text, element) {
    if (!text) return;
    setHoverHint({
      text,
      ...makeTooltipPosition(element),
    });
  }

  function hideHint() {
    setHoverHint(null);
  }

  function updateSetting(key, value) {
    if (typeof setLiquifySettings !== 'function') return;
    setLiquifySettings((current) => ({
      ...(current || {}),
      [key]: normaliseLiquifySettings({ [key]: value })[key],
    }));
  }

  function resetSettings() {
    hideHint();
    if (typeof setLiquifySettings === 'function') {
      setLiquifySettings((current) => ({
        ...(current || {}),
        ...DEFAULT_LIQUIFY_SETTINGS,
      }));
    }
    if (typeof setLiquifyMode === 'function') {
      setLiquifyMode(normaliseLiquifyMode('flow_shift'));
    }
  }

  return (
    <div
      style={{
        borderRadius: 14,
        border: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.04)',
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 9,
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 950,
              color: 'rgba(255,255,255,0.96)',
              lineHeight: 1.15,
            }}
          >
            Distortion
          </div>
          <div
            title={activeMode.description || activeMode.label}
            onMouseEnter={(event) => showHint(activeMode.description || activeMode.label, event.currentTarget)}
            onMouseLeave={hideHint}
            style={{
              marginTop: 2,
              fontSize: 10,
              fontWeight: 650,
              color: 'rgba(255,255,255,0.52)',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {activeMode.description || activeMode.label}
          </div>
        </div>

        <button
          type="button"
          onClick={resetSettings}
          title={DISTORTION_SETTING_HINTS.reset}
          onMouseEnter={(event) => showHint(DISTORTION_SETTING_HINTS.reset, event.currentTarget)}
          onMouseLeave={hideHint}
          style={{
            height: 26,
            borderRadius: 7,
            border: '1px solid rgba(255,255,255,0.11)',
            background: 'rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.82)',
            padding: '0 9px',
            fontSize: 10,
            fontWeight: 900,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Reset
        </button>
      </div>

      <PanelSlider
        label="Tool size"
        value={brushSize}
        min={1}
        max={2000}
        step={1}
        suffix="px"
        normaliseValue={(value) => Math.max(1, Math.min(2000, Math.round(value)))}
        onChange={setBrushSize}
        hint={DISTORTION_SETTING_HINTS.size}
        onShowHint={showHint}
        onHideHint={hideHint}
      />

      <label
        title={DISTORTION_SETTING_HINTS.mode}
        onMouseEnter={(event) => showHint(DISTORTION_SETTING_HINTS.mode, event.currentTarget)}
        onMouseLeave={hideHint}
        style={{
          display: 'grid',
          gridTemplateColumns: '58px minmax(0, 1fr)',
          alignItems: 'center',
          gap: 8,
          minHeight: 30,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 850,
            color: 'rgba(255,255,255,0.72)',
          }}
        >
          Mode
        </span>
        <select
          value={safeMode}
          onChange={(event) => {
            if (typeof setLiquifyMode === 'function') setLiquifyMode(normaliseLiquifyMode(event.target.value));
          }}
          style={{
            height: 28,
            minWidth: 0,
            width: '100%',
            borderRadius: 7,
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(0,0,0,0.24)',
            color: 'rgba(255,255,255,0.94)',
            padding: '0 8px',
            boxSizing: 'border-box',
            fontSize: 11,
            fontWeight: 850,
          }}
        >
          {DISTORTION_MODES.map((mode) => (
            <option key={mode.id} value={mode.id} title={mode.description || mode.label}>{mode.label}</option>
          ))}
        </select>
      </label>

      <PanelSlider label="Flow" value={safeSettings.flow * 100} onChange={(value) => updateSetting('flow', value)} hint={DISTORTION_SETTING_HINTS.flow} onShowHint={showHint} onHideHint={hideHint} />
      <PanelSlider label="Twist" value={safeSettings.twist * 100} onChange={(value) => updateSetting('twist', value)} hint={DISTORTION_SETTING_HINTS.twist} onShowHint={showHint} onHideHint={hideHint} />
      <PanelSlider label="Chaos" value={safeSettings.chaos * 100} onChange={(value) => updateSetting('chaos', value)} hint={DISTORTION_SETTING_HINTS.chaos} onShowHint={showHint} onHideHint={hideHint} />
      <PanelSlider label="Detail" value={safeSettings.detail * 100} onChange={(value) => updateSetting('detail', value)} hint={DISTORTION_SETTING_HINTS.detail} onShowHint={showHint} onHideHint={hideHint} />
      <PanelSlider label="Edge" value={safeSettings.edge * 100} onChange={(value) => updateSetting('edge', value)} hint={DISTORTION_SETTING_HINTS.edge} onShowHint={showHint} onHideHint={hideHint} />

      <PanelHint hint={hoverHint} />
    </div>
  );
}
